import type { FlipCorner, Point } from '@/types/book';

const DIRECTION_SCORE_WEIGHT = 0.25;
const CORNER_SWITCH_HYSTERESIS_PX = 12;

export function calculateSideEdgeShadowProgress(
  grab: Point,
  finger: Point,
  pageWidth: number
): number {
  return (Math.abs(finger.x - grab.x) / (2 * pageWidth)) * 100;
}

function constrainToCircle(origin: Point, radius: number, point: Point): Point {
  const dx = point.x - origin.x;
  const dy = point.y - origin.y;
  const length = Math.hypot(dx, dy);
  if (length <= radius) return point;
  const scale = radius / length;
  return { x: origin.x + dx * scale, y: origin.y + dy * scale };
}

/**
 * Reflect a real paper corner across the crease between the grabbed edge point and the pointer.
 * The crease is their perpendicular bisector, so the grabbed paper point lands on the pointer.
 */
export function reflectCornerAcrossCrease(corner: Point, grab: Point, finger: Point): Point {
  const dx = finger.x - grab.x;
  const dy = finger.y - grab.y;
  const lengthSquared = dx * dx + dy * dy;
  if (lengthSquared < Number.EPSILON) return { ...corner };

  const midpoint = {
    x: (grab.x + finger.x) / 2,
    y: (grab.y + finger.y) / 2
  };
  const scale =
    (2 * ((corner.x - midpoint.x) * dx + (corner.y - midpoint.y) * dy)) /
    lengthSquared;

  return {
    x: corner.x - scale * dx,
    y: corner.y - scale * dy
  };
}

function scoreCorner(
  corner: FlipCorner,
  grab: Point,
  finger: Point,
  pageWidth: number,
  pageHeight: number
): number {
  const cornerY = corner === 'top' ? 0 : pageHeight;
  const originalCorner = { x: grab.x, y: cornerY };
  const spineX = grab.x <= 0 ? pageWidth : grab.x - pageWidth;
  const spineAnchor = { x: spineX, y: cornerY };
  const rawCorner = reflectCornerAcrossCrease(originalCorner, grab, finger);
  const constrainedCorner = constrainToCircle(spineAnchor, pageWidth, rawCorner);
  const constrainedGrab = reflectCornerAcrossCrease(grab, originalCorner, constrainedCorner);
  const constraintError = Math.hypot(
    constrainedGrab.x - finger.x,
    constrainedGrab.y - finger.y
  );
  const dy = finger.y - grab.y;
  const directionScore = (corner === 'top' ? -dy : dy) * DIRECTION_SCORE_WEIGHT;

  return constraintError + directionScore;
}

/**
 * Compare the top- and bottom-anchored rigid solutions on every frame.
 * Keep the current corner until the alternative is clearly better to avoid midpoint jitter.
 */
export function pickAdaptiveSideEdgeCorner(
  grab: Point,
  finger: Point,
  pageWidth: number,
  pageHeight: number,
  currentCorner: FlipCorner | null
): FlipCorner {
  const topScore = scoreCorner('top', grab, finger, pageWidth, pageHeight);
  const bottomScore = scoreCorner('bottom', grab, finger, pageWidth, pageHeight);

  if (!currentCorner) {
    if (Math.abs(topScore - bottomScore) < Number.EPSILON) {
      return finger.y <= pageHeight / 2 ? 'top' : 'bottom';
    }
    return topScore < bottomScore ? 'top' : 'bottom';
  }

  const currentScore = currentCorner === 'top' ? topScore : bottomScore;
  const candidate: FlipCorner = topScore < bottomScore ? 'top' : 'bottom';
  const candidateScore = candidate === 'top' ? topScore : bottomScore;

  if (candidate !== currentCorner && candidateScore + CORNER_SWITCH_HYSTERESIS_PX < currentScore) {
    return candidate;
  }
  return currentCorner;
}
